import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { RadioGroup, Typography } from '@mui/material';
import { DateRange, Error, Place } from '@mui/icons-material';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { STUDENT_CARD_BOOKING_TEXT } from './lang';
import { MERIDIEM_TYPE } from '../constants';
import {
  type StudentBookInfo,
  type StudentSelectedTime,
  type CollectionTimeSlot,
  useConfirmCollectionTime,
  useCancelCollectionTime,
} from '../../../graphql';
import {
  Accordion,
  Button,
  Radio,
  Alert,
  type AlertDetails,
  Loading,
  Dialog,
} from '../../../../../components';

type StudentCardBookingFormProps = {
  studentNumber: string;
  entryYear: string;
  studentBookInfo: StudentBookInfo;
  studentSelectedTime: StudentSelectedTime;
  refetch: () => void;
};

type StudentBookFormFormik = {
  selectedDate: string;
  selectedTime: string;
};

export const StudentCardBookingForm: React.FC<StudentCardBookingFormProps> = ({
  studentNumber,
  studentBookInfo,
  studentSelectedTime,
  entryYear,
  refetch,
}: StudentCardBookingFormProps) => {
  const { applicationId = '' } = useParams();
  const { applicantName, cardCollectionSlots, closedNotes } = studentBookInfo;
  const { slotId, date } = studentSelectedTime;
  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<{
    content: string;
    handleDialogConfirmAction?: () => void;
  }>({
    content: '',
    handleDialogConfirmAction: undefined,
  });

  const applicantBasicInfo = [
    {
      label: `${STUDENT_CARD_BOOKING_TEXT.APPLICANT_NAME}:`,
      value: applicantName,
    },
    {
      label: `${STUDENT_CARD_BOOKING_TEXT.STUDENT_NUMBER}:`,
      value: studentNumber,
    },
  ];

  const confirmCollectionTime = useConfirmCollectionTime();
  const handleConfirmSchedule = async (): Promise<void> => {
    const { selectedTime } = formik.values;
    setLoading(true);
    setDialogOpen(false);
    try {
      await confirmCollectionTime({
        variables: {
          applicationId,
          slotId: selectedTime,
        },
      });
      alertDetailDef = {
        alertContent: STUDENT_CARD_BOOKING_TEXT.CONFIRM_SELECTED_TIME_ALERT,
        alertType: 'success',
      };
      refetch();
    } catch (err) {
      alertDetailDef = {
        alertContent: 'Error Occurs.' + (err as Error)?.message,
        alertType: 'error',
      };
    } finally {
      setLoading(false);
      setAlertDetails(alertDetailDef);
      setOpenAlert(true);
    }
  };

  const cancelCollectionTime = useCancelCollectionTime();
  const handleCancelSchedule = async (): Promise<void> => {
    setLoading(true);
    setDialogOpen(false);
    try {
      await cancelCollectionTime({
        variables: {
          applicationId,
          slotId,
        },
      });
      alertDetailDef = {
        alertContent: STUDENT_CARD_BOOKING_TEXT.CANCEL_SELECTED_TIME_ALERT,
        alertType: 'success',
      };
      refetch();
      // Reset form values to empty, can't use formik.resetForm() as it will reset the form to initial values
      await formik.setValues({
        selectedDate: '',
        selectedTime: '',
      });
    } catch (err) {
      alertDetailDef = {
        alertContent: 'Error Occurs.' + (err as Error)?.message,
        alertType: 'error',
      };
    } finally {
      setLoading(false);
      setAlertDetails(alertDetailDef);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setOpenAlert(true);
    }
  };

  const cardCollectionDialogContent = {
    confirmSchedule: {
      content: STUDENT_CARD_BOOKING_TEXT.CONFIRM_SELECT_TIME,
      handleDialogConfirmAction: handleConfirmSchedule,
    },
    cancelSchedule: {
      content: STUDENT_CARD_BOOKING_TEXT.CANCEL_SELECT_TIME,
      handleDialogConfirmAction: handleCancelSchedule,
    },
  };

  const openConfirmScheduleDialog = (): void => {
    setDialogContent({
      ...cardCollectionDialogContent.confirmSchedule,
    });
    setDialogOpen(true);
  };

  const openCancelScheduleDialog = (): void => {
    setDialogContent({
      ...cardCollectionDialogContent.cancelSchedule,
    });
    setDialogOpen(true);
  };

  const formik = useFormik<StudentBookFormFormik>({
    initialValues: {
      selectedDate: date,
      selectedTime: slotId,
    },
    validationSchema: object().shape({
      selectedDate: string().required(),
      selectedTime: string().required(),
    }),
    onSubmit: openConfirmScheduleDialog,
  });

  const processTimeSlot = (meridiem: MERIDIEM_TYPE): CollectionTimeSlot[] => {
    return (
      (
        cardCollectionSlots.filter(
          slot => slot.date === formik.values.selectedDate, // different date may have different time slot
        )?.[0]?.slots ?? []
      )?.filter(slot => slot.meridiem === meridiem) ?? []
    );
  };

  const timeAMSlot = processTimeSlot(MERIDIEM_TYPE.AM);
  const timePMSlot = processTimeSlot(MERIDIEM_TYPE.PM);

  const hasConfirmCollectTime = !!date || !!slotId;
  const displayTime =
    cardCollectionSlots
      .find(slot => slot.date === date)
      ?.slots.find(slot => slot.slotId === slotId)?.slotName ?? '';
  const allExpired = cardCollectionSlots.every(slot => slot.allExpired);

  const disabledButtonAction = allExpired || hasConfirmCollectTime;
  const expiredWithoutConfirm = allExpired && !hasConfirmCollectTime;

  const resetTimeSlot = async (): Promise<void> => {
    formik.resetForm();
  };

  const handleChangeDate = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const selectedDate = event.target.value;
    await formik.setValues({
      selectedDate,
      selectedTime: '', // reset time slot when date change
    });
    await formik.setTouched({});
  };

  const displayTimeSlot = (
    slots: CollectionTimeSlot[],
    title: string,
  ): React.ReactNode => {
    return (
      slots.length > 0 && (
        <Accordion
          title={title}
          defaultExpanded
          disableExpand
          sx={{
            margin: '0 !important',
            '.MuiButtonBase-root': {
              minHeight: '2.5rem !important',
            },
            '.MuiAccordionSummary-content': {
              margin: '0 !important',
            },
          }}
          details={
            <RadioGroup
              name="selectedTime"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0.5rem 1rem',
              }}
              onChange={formik.handleChange}
            >
              {slots.map((time, index) => {
                const { isAvailable, isExpired } = time;

                // display expired first once the time slot is both expired and full
                const displayText = isExpired
                  ? STUDENT_CARD_BOOKING_TEXT.EXPIRED
                  : isAvailable
                    ? ''
                    : STUDENT_CARD_BOOKING_TEXT.FULL;
                const disableField =
                  isExpired || !isAvailable || hasConfirmCollectTime;

                return (
                  <div key={index}>
                    <Radio
                      size="small"
                      label={time.slotName}
                      value={time.slotId}
                      disabled={disableField}
                      checked={formik.values.selectedTime === time.slotId}
                      onBlur={formik.handleBlur}
                      error={
                        !disableField &&
                        formik.touched.selectedTime &&
                        Boolean(formik.errors.selectedTime)
                      }
                    />
                    <span className="italic text-[#951D1D]">{displayText}</span>
                  </div>
                );
              })}
            </RadioGroup>
          }
        />
      )
    );
  };

  const openBook = cardCollectionSlots?.length;

  return (
    <>
      {loading && <Loading />}
      {/** Applicant basic info */}
      <div className="bg-[#F6F6F6] grid grid-cols-2 mb-4">
        {applicantBasicInfo.map((info, index) => (
          <div key={index} className="flex flex-row p-4">
            <div className="text-[#951D1D] font-medium mr-2">{info.label}</div>
            <div>{info.value}</div>
          </div>
        ))}
      </div>
      {/** Date Selection */}
      {openBook ? (
        <>
          <form onSubmit={formik.handleSubmit}>
            <div className="text-[#951D1D] font-medium">
              {STUDENT_CARD_BOOKING_TEXT.SELECT_PREFERRED_TIME}
            </div>
            <Accordion
              title={STUDENT_CARD_BOOKING_TEXT.DATE}
              defaultExpanded
              disableExpand
              sx={{
                '.MuiButtonBase-root': {
                  minHeight: '2.5rem !important',
                },
                '.MuiAccordionSummary-content': {
                  margin: '0 !important',
                },
              }}
              details={
                <RadioGroup
                  name="selectedDate"
                  onChange={handleChangeDate}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '4rem',
                    padding: '0.5rem 1rem',
                  }}
                >
                  {cardCollectionSlots.map((date, index) => {
                    const dateExpired = date.allExpired;
                    const displayText = dateExpired
                      ? STUDENT_CARD_BOOKING_TEXT.EXPIRED
                      : '';
                    const disabledField = dateExpired || hasConfirmCollectTime;
                    return (
                      <div key={index}>
                        <Radio
                          size="small"
                          label={date.date}
                          value={date.date}
                          disabled={disabledField}
                          checked={formik.values.selectedDate === date.date}
                          onBlur={formik.handleBlur}
                          error={
                            !disabledField &&
                            formik.touched.selectedDate &&
                            Boolean(formik.errors.selectedDate)
                          }
                        />
                        <span className="italic text-[#951D1D]">
                          {displayText}
                        </span>
                      </div>
                    );
                  })}
                </RadioGroup>
              }
            />

            {/** Time Selection */}
            <div className="grid grid-cols-2 gap-4">
              {displayTimeSlot(timeAMSlot, STUDENT_CARD_BOOKING_TEXT.AM)}
              {displayTimeSlot(timePMSlot, STUDENT_CARD_BOOKING_TEXT.PM)}
            </div>

            <div className="flex flex-row items-center justify-center w-full mt-8 gap-4">
              <Button
                variant="contained"
                type="submit"
                disabled={disabledButtonAction}
                sx={{
                  textTransform: 'none',
                }}
              >
                {STUDENT_CARD_BOOKING_TEXT.CONFIRM}
              </Button>
              <Button
                variant="outlined"
                disabled={disabledButtonAction}
                sx={{
                  textTransform: 'none',
                }}
                onClick={resetTimeSlot}
              >
                {STUDENT_CARD_BOOKING_TEXT.CANCEL}
              </Button>
            </div>
          </form>

          {hasConfirmCollectTime && (
            <>
              <div className="flex flex-row items-center rounded border-2 border-[#951D1D] p-4 mt-10">
                <Place
                  color="primary"
                  sx={{
                    fontSize: '3rem',
                  }}
                />
                <div className="flex flex-col ml-4">
                  <div className="text-[#951D1D] font-medium">
                    {STUDENT_CARD_BOOKING_TEXT.PICKUP_VENUE_TITLE}
                  </div>
                  <div className="text-[#951D1D]">
                    {STUDENT_CARD_BOOKING_TEXT.PICKUP_VENUE_LOCATION}
                  </div>
                </div>
              </div>

              <div className="bg-[#F6F6F6] p-4 mt-6">
                <div className="text-[#951D1D] font-medium mb-2">
                  {STUDENT_CARD_BOOKING_TEXT.SELECTED_TIME_SLOT}
                </div>
                <div className="flex flex-row bg-[#FFFFFF] p-4 items-center">
                  <DateRange color="primary" />
                  <span className="font-medium ml-4 mr-4">{date}</span>
                  <span className="font-medium ml-4 mr-8">{displayTime}</span>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      height: '2rem',
                    }}
                    onClick={openCancelScheduleDialog}
                  >
                    {STUDENT_CARD_BOOKING_TEXT.CANCEL}
                  </Button>
                </div>
              </div>

              <div className="flex flex-row text-[#951D1D] mt-4 items-start font-medium italic">
                <Error color="primary" />
                <span className="ml-2">
                  {STUDENT_CARD_BOOKING_TEXT.QR_CODE_INFO}
                </span>
              </div>
            </>
          )}

          {expiredWithoutConfirm && (
            <div className="flex flex-row text-[#951D1D] mt-4 font-medium italic">
              <Error color="primary" />
              <div
                dangerouslySetInnerHTML={{
                  __html: STUDENT_CARD_BOOKING_TEXT.EMAIL_INFO,
                }}
                className="ml-2"
              />
            </div>
          )}

          <Alert
            openAlert={openAlert}
            alertDetails={alertDetails}
            setOpenAlert={setOpenAlert}
          />

          <Dialog
            openDialog={dialogOpen}
            dialogTitle={STUDENT_CARD_BOOKING_TEXT.NOTIFICATION}
            dialogAction={STUDENT_CARD_BOOKING_TEXT.YES}
            cancelAction={STUDENT_CARD_BOOKING_TEXT.CANCEL}
            onDialogAction={dialogContent.handleDialogConfirmAction}
            onDialogClose={() => {
              setDialogOpen(false);
            }}
          >
            <Typography margin={'0rem 1.5rem 2rem'}>
              {dialogContent.content}
            </Typography>
          </Dialog>
        </>
      ) : (
        <p style={{ color: 'red' }}>
          {closedNotes ?? `${STUDENT_CARD_BOOKING_TEXT.TIME_SLOT_OPEN_SOON}`}
        </p>
      )}
    </>
  );
};
